/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {getAuthHttpBuffer} from '../auth/auth-http-buffer.service';
import {registerLastViewedFromResourceGet} from '../session/user-profile-saver.service';
import {ApiConfig} from '../api/api';
import {OBJECT_STATE} from '../api/resources/resources-constants';
import {
  AdnHttpRequest,
  AdnHttpResponse,
  AdnHttpService,
  AdnHttpProgressCallback
} from './adn-http.service';
export type AdnHttpSender = (config: AdnHttpRequest) => Promise<any>;
export type AdnHttpResponseSender = (config: AdnHttpRequest) => Promise<AdnHttpResponse>;
export type AdnHttpUploadSender = (
  config: AdnHttpRequest,
  onProgress?: AdnHttpProgressCallback
) => Promise<AdnHttpResponse>;

export interface AdnHttpResourceOptions {
  uri?: string;
  responseType?: 'json' | 'text';
  transformRequest?: (data: any) => any;
  transformResponse?: (data: any) => any;
  /**
   * Fields copied from the save body onto the request query string
   * (ngResource `@param` behaviour — e.g. line item ignoreProduct).
   */
  saveQueryParams?: string[];
}

export interface AdnHttpResourceActionOptions {
  method: 'GET' | 'HEAD' | 'POST' | 'PUT' | 'DELETE';
  url: string;
  defaultParams?: Record<string, any>;
  queryParams?: string[];
  hasBody?: boolean;
  responseType?: 'json' | 'text';
  transformRequest?: (data: any) => any;
  transformResponse?: (data: any) => any;
}

export type LocalUserProfileRef = any | (() => any);

export function createAdnHttpResourceAction(
  send: AdnHttpSender,
  options: AdnHttpResourceActionOptions
): (...args: any[]) => any {
  const hasBody = options.hasBody === undefined
    ? options.method !== 'GET' && options.method !== 'HEAD'
    : options.hasBody;
  const transformRequest = options.transformRequest || _.identity;
  const transformResponse = options.transformResponse || _.identity;

  return function(a1?: any, a2?: any, a3?: any, a4?: any): any {
    let params: any = {};
    let data: any;
    let success: any;
    let error: any;

    if (!hasBody) {
      params = _.isFunction(a1) ? {} : (a1 || {});
      success = _.isFunction(a1) ? a1 : a2;
      error = _.isFunction(a1) ? a2 : a3;
    } else if (_.isFunction(a1)) {
      data = {};
      success = a1;
      error = a2;
    } else if (_.isFunction(a2)) {
      data = a1 || {};
      success = a2;
      error = a3;
    } else if (_.isFunction(a3)) {
      params = a1 || {};
      data = a2 || {};
      success = a3;
      error = a4;
    } else if (a2 !== undefined) {
      params = a1 || {};
      data = a2 || {};
    } else {
      data = a1 || {};
    }

    const source = _.assign({}, options.defaultParams, data, params);
    const pathParams: string[] = [];
    const url = options.url.replace(/\/:([A-Za-z_][A-Za-z0-9_]*)/g, (_match: string, name: string) => {
      pathParams.push(name);
      return '/' + encodeURIComponent(source[name]);
    });
    const queryParams = options.queryParams
      ? _.pick(source, options.queryParams)
      : (hasBody ? _.omit(params, pathParams) : _.omit(source, pathParams));
    const request: AdnHttpRequest = {
      method: options.method,
      url,
      params: _.omitBy(queryParams, _.isUndefined),
      responseType: options.responseType
    };
    if (hasBody) {
      request.body = transformRequest(data);
    }

    const promise = send(request)
      .then(transformResponse)
      .then((response: any) => response === null || response === undefined ? {} : response);
    // Match ngResource action shape so list helpers / promise-spinners can watch $resolved.
    const value: any = {$resolved: false};
    value.$promise = promise.then((response: any) => {
      if (_.isObject(response) && !_.isArray(response)) {
        _.assign(value, response);
      }
      // AdnHttp settles after HttpClient's zoneless tick — sync $resolved is safe.
      value.$resolved = true;
      (success || _.noop)(response);
      return response;
    }, (err: any) => {
      value.$resolved = true;
      (error || _.noop)(err);
      return Promise.reject(err);
    });
    return value;
  };
}

/**
 * Composes AdnHttpService with the app-wide auth-header injection and API error
 * handling, so every resource gets both for free
 * without duplicating that composition per resource.
 */
export function createAdnHttpResponseSender(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: any,
  ApiErrorHandler: any
): AdnHttpResponseSender {
  return function send(config: AdnHttpRequest): Promise<AdnHttpResponse> {
    const merged: AdnHttpRequest = authHttpInterceptor.request(_.assign({params: {}, headers: {}}, config));
    return AdnHttp.request(merged).then((response: any) => {
      ApiErrorHandler.response(response);
      return response;
    }, (response: any) => {
      if (response.xhrStatus === 'abort') {
        return Promise.reject(response);
      }
      const authHttpBuffer = getAuthHttpBuffer();
      if (response.status === 401 && authHttpBuffer && !merged.ignoreAuthModule) {
        return authHttpBuffer.bufferRequest(merged, response).then((retried: any) => {
          ApiErrorHandler.response(retried);
          return retried;
        });
      }
      return ApiErrorHandler.responseError(response);
    });
  };
}

export function createAdnHttpResourceSender(AdnHttp: AdnHttpService, authHttpInterceptor: any, ApiErrorHandler: any): AdnHttpSender {
  const sendResponse = createAdnHttpResponseSender(AdnHttp, authHttpInterceptor, ApiErrorHandler);
  return (config: AdnHttpRequest) => sendResponse(config).then((response) => response.data);
}

export function createAdnHttpUploadSender(
  AdnHttp: AdnHttpService,
  authHttpInterceptor: any,
  ApiErrorHandler: any
): AdnHttpUploadSender {
  return function upload(config: AdnHttpRequest, onProgress?: AdnHttpProgressCallback): Promise<AdnHttpResponse> {
    const request = authHttpInterceptor.request(_.assign({params: {}, headers: {}}, config));
    const upload = onProgress ? AdnHttp.upload(request, onProgress) : AdnHttp.upload(request);
    return upload.then((response: AdnHttpResponse) => {
      ApiErrorHandler.response(response);
      return response;
    }, (response: any) => {
      const authHttpBuffer = getAuthHttpBuffer();
      if (response.status === 401 && authHttpBuffer && !request.ignoreAuthModule) {
        return authHttpBuffer.bufferRequest(request, response).then((retried: any) => {
          ApiErrorHandler.response(retried);
          return retried;
        });
      }
      return ApiErrorHandler.responseError(response);
    });
  };
}

export function createAdnUploadFormData(data: Record<string, any>): FormData {
  const formData = new FormData();
  _.forEach(data, (value: any, key: string) => {
    if (value === undefined || value === null) {
      return;
    }
    if (value instanceof Blob) {
      formData.append(key, value, (value as File).name);
    } else if (_.isObject(value)) {
      formData.append(key, JSON.stringify(value));
    } else {
      formData.append(key, String(value));
    }
  });
  return formData;
}

function deriveResourceName(basePath: string): string {
  const pathNoSlash = basePath.replace(/\//g, '');
  return pathNoSlash.lastIndexOf('s') === pathNoSlash.length - 1
    ? pathNoSlash.substring(0, pathNoSlash.length - 1)
    : pathNoSlash;
}

/**
 * Rebuilds the ngResource-shaped contract that adnResource used to provide
 * (constructor, query/get/save/archive/delete/activate/bulkSave statics,
 * $save/$archive/$delete/$activate/$head/savePartial instance methods,
 * resourceName) on top of a pure-Angular sender. Drop-in for the shared admin
 * list/edit-page machinery (list-helper.js, AdminResourceEditPageBase,
 * create-resource-copy.ts, model-comms-actions.ts).
 */
export function createAdnHttpResource(
  send: AdnHttpSender,
  basePath: string,
  localUserProfile: LocalUserProfileRef,
  resourceNameOverride?: string,
  defaultQueryParams?: Record<string, any>,
  options?: AdnHttpResourceOptions
): any {
  const uri = options?.uri || ApiConfig.obtain().getVersionedUri(basePath);
  const uriWithId = (id: any) => id === undefined || id === null || id === ''
    ? uri
    : uri + '/' + encodeURIComponent(id);
  const transformRequest = options?.transformRequest || _.identity;
  const transformResponse = options?.transformResponse || _.identity;
  const getLocalUserProfile = () => _.isFunction(localUserProfile)
    ? localUserProfile()
    : localUserProfile;

  function objectStateAction(objectState: string) {
    return function(data: any): any {
      return {
        $promise: send({
          method: 'POST',
          url: uriWithId(data.id),
          params: defaultQueryParams,
          body: {id: data.id, objectState: objectState}
        })
      };
    };
  }

  function Resource(this: any, data?: any) {
    _.assign(this, data);
  }

  (Resource as any).get = function(a1?: any, a2?: any, a3?: any): any {
    let params: any = {};
    let successCb: any;
    let errorCb: any;
    if (_.isFunction(a1)) {
      successCb = a1;
      errorCb = a2;
    } else {
      params = a1 || {};
      successCb = a2;
      errorCb = a3;
    }
    params = _.assign({}, defaultQueryParams, params);
    const instancePromise = send({
      method: 'GET',
      url: uriWithId(params.id),
      params: _.omit(params, 'id'),
      responseType: options?.responseType
    }).then((data: any) => new (Resource as any)(transformResponse(data)));
    return {
      $promise: instancePromise.then((instance: any) => {
        registerLastViewedFromResourceGet(instance);
        (successCb || _.noop)(instance);
        return instance;
      }, (err: any) => {
        (errorCb || _.noop)(err);
        return Promise.reject(err);
      })
    };
  };

  (Resource as any).head = function(id: any): any {
    return {$promise: send({method: 'HEAD', url: uriWithId(id), params: defaultQueryParams})};
  };

  (Resource as any).getLink = function(params: any): any {
    return {
      $promise: send({
        method: 'GET',
        url: uriWithId(params.id) + '/link',
        params: _.assign({}, defaultQueryParams, _.omit(params, 'id'))
      })
        .then((data: any) => new (Resource as any)(data))
    };
  };

  (Resource as any).save = function(a1?: any, a2?: any, a3?: any, a4?: any): any {
    let params: any = {};
    let data: any = {};
    let successCb: any;
    let errorCb: any;
    if (_.isFunction(a1)) {
      successCb = a1;
      errorCb = a2;
    } else if (_.isFunction(a2)) {
      data = a1 || {};
      successCb = a2;
      errorCb = a3;
    } else if (_.isFunction(a3)) {
      params = a1 || {};
      data = a2 || {};
      successCb = a3;
      errorCb = a4;
    } else if (a2 !== undefined) {
      params = a1 || {};
      data = a2 || {};
    } else {
      data = a1 || {};
    }
    // Promote configured body fields to query params (legacy @ignoreProduct etc.).
    const saveQueryFromData = options?.saveQueryParams?.length
      ? _.pick(data, options.saveQueryParams)
      : {};
    const promise = send({
      method: 'POST',
      url: uriWithId(params.id || data.id),
      params: _.assign(
        {concise: data.concise},
        defaultQueryParams,
        saveQueryFromData,
        _.omit(params, 'id')
      ),
      body: transformRequest(data),
      responseType: options?.responseType
    }).then(transformResponse);
    return {
      $promise: promise.then((saved: any) => {
        (successCb || _.noop)(saved);
        return saved;
      }, (err: any) => {
        (errorCb || _.noop)(err);
        return Promise.reject(err);
      })
    };
  };

  (Resource as any).archive = objectStateAction(OBJECT_STATE.inactive);
  (Resource as any).delete = objectStateAction(OBJECT_STATE.hidden);
  (Resource as any).activate = objectStateAction(OBJECT_STATE.active);

  (Resource as any).bulkSave = function(params: any, items?: any[]): any {
    if (items === undefined) {
      items = params;
      params = {};
    }
    return {$promise: send({method: 'POST', url: uri, params: _.assign({}, defaultQueryParams, params), body: items})};
  };

  (Resource as any).query = function(a1: any, a2: any, a3: any): any {
    let params: any = {};
    let success: any;
    let error: any;
    if (_.isFunction(a1)) {
      success = a1;
      error = a2;
    } else {
      params = a1 || {};
      success = a2;
      error = a3;
    }

    const defaultSize = 1001;
    if (_.isArray(params) || _.isArray(_.get(params, 'ids'))) {
      const ids = params.ids || params;
      const itemLength = Math.min(ids.length, defaultSize);
      params = _.merge({
        id: _.take(ids, itemLength).join(';'),
        pageSize: Math.min(ids.length, itemLength)
      }, _.isArray(params) ? {} : _.omit(params, 'ids'));
    }

    params = _.assign({includeHidden: false, includeActive: true, includeInactive: false}, defaultQueryParams, params);
    params.orderBy = params.orderBy || 'name';
    params.pageSize = params.pageSize || defaultSize;

    const value: any = {$resolved: false, results: []};
    // Empty-id short-circuit bypasses AdnHttp settle — macrotask so sync $resolved
    // in .then does not race an in-flight zoneless tick.
    const requestPromise = (_.has(params, 'id') && _.trim(params.id).length === 0)
      ? new Promise((resolve) => setTimeout(() => resolve({results: [], page: 0, totalCount: 0}), 0))
      : send({method: 'GET', url: uri, params: params});

    value.$promise = requestPromise.then((data: any) => {
      _.assign(value, data);
      value.results = _.map(value.results, (item: any) => new (Resource as any)(transformResponse(item)));
      // AdnHttp settles after HttpClient's zoneless tick — sync $resolved is safe.
      value.$resolved = true;
      (success || _.noop)(value);
      return value;
    }, (err: any) => {
      value.$resolved = true;
      (error || _.noop)(err);
      return Promise.reject(err);
    });

    return value;
  };

  Resource.prototype.resourceName = resourceNameOverride || deriveResourceName(basePath);

  Resource.prototype.$save = function(success?: any, error?: any): Promise<any> {
    return (Resource as any).save(_.cloneDeep(this)).$promise.then((data: any) => {
      _.assign(this, data);
      (success || _.noop)(this);
      return this;
    }, (err: any) => {
      (error || _.noop)(err);
      return Promise.reject(err);
    });
  };

  Resource.prototype.$archive = function(): Promise<any> {
    return (Resource as any).archive({id: this.id}).$promise.then((data: any) => {
      _.assign(this, data);
      return this;
    });
  };

  Resource.prototype.$delete = function(): Promise<any> {
    return (Resource as any).delete({id: this.id}).$promise.then((data: any) => {
      _.assign(this, data);
      return this;
    });
  };

  Resource.prototype.$activate = function(): Promise<any> {
    return (Resource as any).activate({id: this.id}).$promise.then((data: any) => {
      _.assign(this, data);
      return this;
    });
  };

  Resource.prototype.$head = function(successCb?: any): Promise<any> {
    return (Resource as any).head(this.id).$promise.then(() => {
      (successCb || _.noop)();
    });
  };

  Resource.prototype.savePartial = function(props: any, endPoint?: string, additionalProps?: any): Promise<any> {
    if (getLocalUserProfile().isReadOnly()) {
      return Promise.reject('Read-only is the sole option');
    }

    let data: any;
    if (props) {
      props = _.isArray(props) ? props.slice() : [props];
      props.unshift('id');
      data = _.pick(this, props);
    } else {
      data = _.cloneDeep(this);
    }
    _.forEach(additionalProps || {}, (val: any, key: string) => {
      data[key] = val;
    });
    return (Resource as any)[endPoint || 'save'](data).$promise;
  };

  Resource.prototype.updateValidationWarnings = function(): void {
    const resource = this;
    (Resource as any).get({id: resource.id}).$promise.then((apiResource: any) => {
      resource.validationWarnings = apiResource.validationWarnings;
    });
  };

  return Resource;
}
